import type { EmployeeTrainingModel } from "@simbelapp/order-sdk";
import { useFormats } from "~/composables/utils/useFormat";
import { isPendingRegistrationRow } from "~/store/date-choice/date-choice.store";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { FilterEnums } from "~/utils/enums/my-activity.enums";
import { RequestSessionStatusEnum, RequestStatus } from "~/utils/enums/request.enums";
import { SessionStatusEnum } from "~/utils/enums/session-details-enums";
import { TrainingPlanStatusEnum } from "~/utils/enums/training-plan-enums";
import type { ColorType } from "~/utils/types/colors.types";

export const useEmployeeTrainings = () => {
  // not used ?
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Demandée le",
      sort: true,
      key: "created_at",
      minWidthPx: 40,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Titre",
      sort: true,
      key: "title",
      minWidthPx: 200,
      maxWidthFr: 1,
      visible: true,
    },
    {
      name: "Statut",
      sort: true,
      key: "status",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: true,
    },
  ]);

  const categoryMapping = (category: string) => {
    return (
      [
        {
          name: "request",
          value: "Demande",
          type: "grey",
          logo: "request",
          state: "Demandée le",
        },
        {
          name: "upcoming",
          value: "À venir",
          type: "grey",
          logo: "date",
          state: "Démarre le",
        },
        {
          name: "ongoing",
          value: "En cours",
          type: "grey",
          logo: "date",
          state: "Démarre le",
        },
        {
          name: "ended",
          value: "Passée",
          type: "grey",
          logo: "graduation_cap_on_v2",
          state: "Finie le",
        },
      ].find((cat) => cat.name === category) || {
        name: "request",
        value: "demande",
        type: "grey",
        logo: "request",
        state: "Demandée le",
      }
    );
  };

  /**
   * Date tag displayed at the bottom of a "Mon activité" card. A registration pending a date choice
   * is carried by a draft: it therefore has neither a start nor an end date. We display
   * "Date à définir" rather than nothing, otherwise the card loses the tag's height and looks
   * truncated next to the others. "À définir" is the wording the mockup uses for these unknown dates.
   */
  function dateTag(training: EmployeeTrainingModel) {
    const formats = useFormats();
    const category = categoryMapping(training.sessionStatus);

    if (training.createdAt && training.sessionStatus) {
      const date =
        category.name === FilterEnums.REQUEST
          ? training.createdAt
          : training.sessionStatus === SessionStatusEnum.ENDED
            ? training.endDate
            : training.startDate;

      if (date) {
        return { type: "light_grey", value: `${category.state} ${formats.toDateDisplay(date)}`, logo: "date" };
      }
    }

    if (isPendingRegistrationRow(training.status)) {
      return { type: "light_grey", value: "Date à définir", logo: "date" };
    }

    return undefined;
  }

  function statusMapping(
    value: string,
    is_Employee_Trainings = false,
  ): {
    name: string;
    value: string;
    type: ColorType;
  } {
    return (
      [
        {
          name: RequestStatus.WAITING_MANAGER_INPUT,
          value: is_Employee_Trainings ? "En attente manager" : "Indications du manager en attente",
          type: "light_blue" as ColorType,
        },
        {
          name: RequestStatus.WAITING_PRIORITY_CONFIRMATION,
          value: "Confirmation priorité en attente",
          type: "light_blue" as ColorType,
        },
        {
          name: RequestStatus.WAITING_QUALIFICATION,
          value: is_Employee_Trainings ? "En attente RH" : "Validation RH en attente",
          type: "light_orange" as ColorType,
        },
        {
          name: RequestStatus.WAITING_HR_VALIDATION,
          value: is_Employee_Trainings ? "En attente RH" : "Validation RH en attente",
          type: "light_orange" as ColorType,
        },
        {
          name: RequestStatus.REQUEST_REFUSED,
          value: is_Employee_Trainings ? "Refusée" : "Demande Refusée",
          type: "light_red" as ColorType,
        },
        {
          name: RequestStatus.REQUEST_CANCELED,
          value: "Annulée",
          type: "light_red" as ColorType,
        },
        {
          name: RequestStatus.VALIDATED,
          value: is_Employee_Trainings ? "En cours de traitement" : "Validée",
          type: is_Employee_Trainings ? "light_blue" : ("light_green" as ColorType),
        },
        {
          name: TrainingPlanStatusEnum.PENDING_PROVIDER,
          value: is_Employee_Trainings ? "En cours de traitement" : "Validée",
          type: is_Employee_Trainings ? "light_blue" : ("light_green" as ColorType),
        },
        {
          name: RequestStatus.NO_VALIDATION_NEEDED,
          value: "Aucune validation nécessaire",
          type: "grey" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.NO_SHOW,
          value: "Absent",
          type: "grey" as ColorType,
        },
        {
          name: RequestStatus.WAITING_FINANCING_VALIDATION,
          value: "En attente de financement",
          type: "light_purple" as ColorType,
        },
        {
          name: RequestStatus.PLANNED,
          value: is_Employee_Trainings ? "En cours de traitement" : "Validée",
          type: is_Employee_Trainings ? "light_blue" : ("light_green" as ColorType),
        },
        {
          name: RequestStatus.ON_HOLD,
          value: "En pause",
          type: "grey" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.PENDING_EMPLOYEE,
          value: "Choix de date en cours",
          type: "light_orange" as ColorType,
        },
        // Registration awaiting a session: the label exposed to the participant is the admin's one
        // (AC-CHOIX.2 / AC-NODATE.10).
        {
          name: TrainingPlanStatusEnum.PENDING_DATE_CHOICE,
          value: "Choix de date en cours",
          type: "light_orange" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.NO_SUITABLE_DATE,
          value: "Aucune date ne convient",
          type: "grey" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.REGISTERED,
          value: "Inscrit",
          type: "light_green" as ColorType,
        },
        {
          name: RequestSessionStatusEnum.COMING,
          value: "À venir",
          type: "grey" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.IN_PROGRESS,
          value: "En cours",
          type: "light_green" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.COMPLETED,
          value: "Terminé",
          type: "green" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.TO_COMPLETE,
          value: "À compléter",
          type: "light_purple" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.FINISHED,
          value: "Passée",
          type: "grey" as ColorType,
        },
        {
          name: TrainingPlanStatusEnum.FINISHED,
          value: "Passée",
          type: "grey" as ColorType,
        },
      ].find((x) => x.name === value) || {
        name: RequestStatus.NO_VALIDATION_NEEDED,
        value: "Aucune validation nécessaire",
        type: "grey" as ColorType,
      }
    );
  }
  return {
    headers,
    statusMapping,
    categoryMapping,
    dateTag,
  };
};
